/**
 * DataTableCell class
 *
 * @package    DataTable
 * @author     Maxim Lukianenko <mlukianenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import TableCell from '@material-ui/core/TableCell';
import DataTableCellLink from './DataTableCells/DataTableCellLink';
import DataTableCellView from './DataTableCells/DataTableCellView';
import DataTableCellAuthor from './DataTableCells/DataTableCellAuthor';
import DataTableCellActions from './DataTableCells/DataTableCellActions';
import DataTableCellProcessStatus from './DataTableCells/DataTableCellProcessStatus';
import DataTableCellIconStatus from './DataTableCells/DataTableCellIconStatus';
import { activeStatusToString } from 'Utils/activeStatusToString';

import {
    DATE_FORMAT,
    DATA_TABLE_COLUMN_TYPE_DATE,
    DATA_TABLE_COLUMN_TYPE_NUMBER,
    DATA_TABLE_COLUMN_TYPE_LINK,
    DATA_TABLE_COLUMN_TYPE_VIEW,
    DATA_TABLE_COLUMN_TYPE_STATUS,
    DATA_TABLE_COLUMN_TYPE_HIDDEN,
    DATA_TABLE_COLUMN_TYPE_AUTHOR,
    DATA_TABLE_COLUMN_TYPE_PROCESS_STATUS,
    DATA_TABLE_COLUMN_TYPE_ACTIONS,
    DATA_TABLE_COLUMN_TYPE_ICON_STATUS,
    DATA_TABLE_COLUMN_TYPE_FORMATTED_MESSAGE,
} from 'Constants';
import { DataTableColumnType, DataTableItemType } from 'Types';
import DataTableCellFormattedMessage
    from 'Components/DataTable/DataTableBody/DataTableCells/DataTableCellFormattedMessage';

export default class DataTableCell extends PureComponent {
    /**
     * @type {Object}
     */
    static contextTypes = {
        intl: PropTypes.object.isRequired,
    };

    /**
     * Handlers
     *
     * @type {{}}
     */
    handlers = {
        intl: (item, field) => {
            return this.context.intl.formatMessage({ id: `data_table.${field}.${item[field]}` });
        },
    };

    /**
     * PropTypes
     */
    static propTypes = {
        column: DataTableColumnType,
        value: PropTypes.any,
        item: DataTableItemType,
    };

    /**
     * Process Handler
     *
     * @param column
     * @param value
     * @param item
     * @return {*}
     */
    processHandler = (column, value, item) => {
        let result;
        if (typeof column.handler === 'function') {
            result = column.handler(item);
        } else if (this.handlers[column.handler]) {
            result = this.handlers[column.handler](item);
        } else {
            result = value;
        }

        return result;
    };

    /**
     * Process Types
     *
     * @param column
     * @param value
     * @param item
     * @return {*}
     */
    processTypes = (column, value, item) => {
        switch (column.type) {
            case DATA_TABLE_COLUMN_TYPE_HIDDEN:
                return null;

            case DATA_TABLE_COLUMN_TYPE_STATUS:
                return activeStatusToString(value);

            case DATA_TABLE_COLUMN_TYPE_DATE:
                return value ? moment(value).format(DATE_FORMAT) : '';

            case DATA_TABLE_COLUMN_TYPE_NUMBER:
                return parseInt(value);

            case DATA_TABLE_COLUMN_TYPE_AUTHOR:
                return <DataTableCellAuthor item={item} value={value} column={column} />;

            case DATA_TABLE_COLUMN_TYPE_PROCESS_STATUS:
                return <DataTableCellProcessStatus item={item} value={value} column={column} />;

            case DATA_TABLE_COLUMN_TYPE_ICON_STATUS:
                return <DataTableCellIconStatus item={item} value={value} column={column} />;

            case DATA_TABLE_COLUMN_TYPE_LINK:
                return <DataTableCellLink item={item} value={value} column={column} />;

            case DATA_TABLE_COLUMN_TYPE_VIEW:
                return <DataTableCellView item={item} value={value} column={column} />;

            case DATA_TABLE_COLUMN_TYPE_ACTIONS:
                return <DataTableCellActions item={item} column={column} />;

            case DATA_TABLE_COLUMN_TYPE_FORMATTED_MESSAGE:
                return <DataTableCellFormattedMessage item={item} value={value} column={column} />;
        }

        return value;
    };

    /**
     * Render
     *
     * @return {*}
     */
    render() {
        const { column, item } = this.props;
        let { value } = this.props;
        if (column.handler) {
            value = this.processHandler(column, value, item);
        } else {
            value = this.processTypes(column, value, item);
        }

        return value !== null ?
            <TableCell className={'mm-table-cell' + (column.type === DATA_TABLE_COLUMN_TYPE_ICON_STATUS ? ' mm-table-cell-icons' : '')}>
                {value}
            </TableCell> :
            value;
    }
}



// WEBPACK FOOTER //
// ./src/components/DataTable/DataTableBody/DataTableCell.jsx